// Typed fetch wrappers for /api/provenance/*.
// Mirrors apps/worker/src/modules/provenance/handlers.ts.

const API_BASE = (import.meta.env.VITE_API_BASE ?? "").replace(/\/$/, "");
const apiUrl = (path: string) => `${API_BASE}${path}`;
const fetchInit: RequestInit = API_BASE ? { credentials: "include" } : {};

export interface DocumentSummary {
  id: string;
  title: string;
  wordCount: number;
  charCount: number;
  updatedAt: number;
  /** The assignment this document is for. Null for documents written before
   *  writing was tied to assignments — those keep working as they did. */
  assignmentId: string | null;
}

export interface DocumentDTO {
  id: string;
  courseId: string;
  ownerUserId: string;
  title: string;
  bodyJson: unknown;
  wordCount: number;
  charCount: number;
  /** How this document's edits are logged; see migration 0025. */
  eventCoords: "pm" | "text";
  createdAt: number;
  updatedAt: number;
  /** See DocumentSummary.assignmentId. */
  assignmentId: string | null;
}

/**
 * Error carrying the HTTP status, so callers branch on the status code rather
 * than on the message text. The worker's 401 body is `{error: "Unauthorized"}`,
 * so a caller testing the message for "401" silently never matches — that
 * exact mistake dead-ended every signed-out student on the attendance
 * check-in. Message text is display copy; `status` and `code` are the contract.
 */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    /** Stable machine-readable code from the server, when it sends one. */
    readonly code?: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function readError(res: Response): Promise<{ message: string; code?: string }> {
  try {
    const body = (await res.json()) as { error?: string; code?: string };
    if (body.error) return { message: body.error, code: body.code };
  } catch {
    /* fall through */
  }
  return { message: `${res.status} ${res.statusText}` };
}

/** Build an ApiError from a non-ok response, preserving status and code. */
async function apiError(res: Response): Promise<ApiError> {
  const { message, code } = await readError(res);
  return new ApiError(message, res.status, code);
}

/** True when an error means "no valid session" — the caller should send the
 *  user to sign in rather than render the message. */
export function isAuthError(e: unknown): boolean {
  return e instanceof ApiError && e.status === 401;
}

let redirectingToLogin = false;

/**
 * Send the user through sign-in, preserving where they were so the callback
 * returns them here. Guarded so several concurrent 401s (the editor fires
 * document loads, saves and event flushes independently) can't each kick off
 * a navigation and race the OIDC state cookie.
 */
export function redirectToLogin(): void {
  if (redirectingToLogin) return;
  redirectingToLogin = true;
  const here = window.location.pathname + window.location.search;
  // /auth/login is outside the SPA, so this must be a full navigation.
  window.location.href = `/auth/login?return_to=${encodeURIComponent(here)}`;
}

export async function listDocuments(
  courseId: string,
  signal?: AbortSignal,
): Promise<DocumentSummary[]> {
  const res = await fetch(
    apiUrl(`/api/provenance/documents?courseId=${encodeURIComponent(courseId)}`),
    { ...fetchInit, signal },
  );
  if (!res.ok) throw await apiError(res);
  const body = (await res.json()) as { documents: DocumentSummary[] };
  return body.documents;
}

/**
 * Open the caller's document for an assignment, creating it the first time.
 * New writing always belongs to an assignment; asking twice returns the same
 * document.
 */
export async function openAssignmentDocument(
  courseId: string,
  assignmentId: string,
): Promise<DocumentDTO> {
  const res = await fetch(apiUrl(`/api/provenance/documents`), {
    ...fetchInit,
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ courseId, assignmentId }),
  });
  if (!res.ok) throw await apiError(res);
  const body = (await res.json()) as { document: DocumentDTO };
  return body.document;
}

export async function getDocument(
  courseId: string,
  id: string,
  signal?: AbortSignal,
): Promise<DocumentDTO> {
  const res = await fetch(
    apiUrl(
      `/api/provenance/documents/${encodeURIComponent(id)}?courseId=${encodeURIComponent(courseId)}`,
    ),
    { ...fetchInit, signal },
  );
  if (!res.ok) throw await apiError(res);
  const body = (await res.json()) as { document: DocumentDTO };
  return body.document;
}

export async function updateDocument(
  id: string,
  patch: {
    courseId: string;
    title?: string;
    bodyJson?: unknown;
    wordCount?: number;
    charCount?: number;
  },
): Promise<DocumentDTO> {
  const res = await fetch(apiUrl(`/api/provenance/documents/${encodeURIComponent(id)}`), {
    ...fetchInit,
    method: "PATCH",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw await apiError(res);
  const body = (await res.json()) as { document: DocumentDTO };
  return body.document;
}

export type ProvenanceEventKind =
  | "insert"
  | "delete"
  | "paste"
  | "llm_insert"
  | "replace"
  | "move";
export type ProvenanceOrigin = "human" | "llm" | "pasted" | "edited";

/** One run of identical origins (compact transport for a range). */
export interface OriginRunDTO {
  origin: ProvenanceOrigin;
  length: number;
}

export interface OutboundEvent {
  clientSeq: number;
  kind: ProvenanceEventKind;
  offset: number;
  length: number;
  text?: string;
  origin?: ProvenanceOrigin;
  sourceMessageId?: string;
  timingBlob?: string;
  /** Origins carried by a deleted range (delete). */
  removedOrigins?: OriginRunDTO[];
  /** Origins to restore for text moved within the document (move). */
  restoredOrigins?: OriginRunDTO[];
}

export async function postEvents(
  documentId: string,
  courseId: string,
  events: OutboundEvent[],
): Promise<{ inserted: number; maxClientSeq: number }> {
  const res = await fetch(
    apiUrl(`/api/provenance/documents/${encodeURIComponent(documentId)}/events`),
    {
      ...fetchInit,
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ courseId, events }),
    },
  );
  if (!res.ok) throw await apiError(res);
  return (await res.json()) as { inserted: number; maxClientSeq: number };
}

export async function deleteDocument(courseId: string, id: string): Promise<void> {
  const res = await fetch(
    apiUrl(
      `/api/provenance/documents/${encodeURIComponent(id)}?courseId=${encodeURIComponent(courseId)}`,
    ),
    { ...fetchInit, method: "DELETE" },
  );
  if (!res.ok) throw await apiError(res);
}

// ─── Chat (slice 3) ─────────────────────────────────────────────────────

export interface AgentSummary {
  id: string;
  name: string;
  mine: boolean;
}

export interface AgentDTO {
  id: string;
  courseId: string;
  ownerUserId: string | null;
  name: string;
  systemPrompt: string;
  createdAt: number;
  updatedAt: number;
}

export interface ConversationDTO {
  id: string;
  documentId: string;
  agentId: string | null;
  agentName: string;
  title: string | null;
  createdAt: number;
  updatedAt: number;
}

export interface MessageDTO {
  id: string;
  role: "user" | "assistant";
  content: string;
  seq: number;
  createdAt: number;
}

/** The agent list plus the course's chat controls (migration 0026), so the
 *  chat panel decides picker-vs-locked without a second fetch. For a student
 *  in a voice-locked course, `agents` is already just the assigned one —
 *  filtered server-side, not merely hidden here. */
export interface AgentListing {
  agents: AgentSummary[];
  /** Whether the LLM chat is on (for the document asked about, else course). */
  chatEnabled: boolean;
  /** Non-null = every student gets this one agent; null = students choose. */
  lockedAgentId: string | null;
}

/**
 * The agents a caller can chat with, plus the chat policy. With `documentId`,
 * the policy is that document's assignment's; without it, the course-level
 * policy older documents follow.
 */
export async function listAgentsWithSettings(
  courseId: string,
  signal?: AbortSignal,
  documentId?: string,
): Promise<AgentListing> {
  const qs = new URLSearchParams({ courseId });
  if (documentId) qs.set("documentId", documentId);
  const res = await fetch(apiUrl(`/api/provenance/agents?${qs}`), { ...fetchInit, signal });
  if (!res.ok) throw await apiError(res);
  const body = (await res.json()) as {
    agents: AgentSummary[];
    chatEnabled?: boolean;
    lockedAgentId?: string | null;
  };
  return {
    agents: body.agents,
    chatEnabled: body.chatEnabled ?? true,
    lockedAgentId: body.lockedAgentId ?? null,
  };
}

export async function listAgents(courseId: string, signal?: AbortSignal): Promise<AgentSummary[]> {
  return (await listAgentsWithSettings(courseId, signal)).agents;
}

export async function getAgent(courseId: string, id: string, signal?: AbortSignal): Promise<AgentDTO> {
  const res = await fetch(
    apiUrl(
      `/api/provenance/agents/${encodeURIComponent(id)}?courseId=${encodeURIComponent(courseId)}`,
    ),
    { ...fetchInit, signal },
  );
  if (!res.ok) throw await apiError(res);
  const body = (await res.json()) as { agent: AgentDTO };
  return body.agent;
}

export async function createAgent(params: {
  courseId: string;
  name: string;
  systemPrompt: string;
  courseDefault?: boolean;
}): Promise<AgentDTO> {
  const res = await fetch(apiUrl(`/api/provenance/agents`), {
    ...fetchInit,
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(params),
  });
  if (!res.ok) throw await apiError(res);
  const body = (await res.json()) as { agent: AgentDTO };
  return body.agent;
}

export async function updateAgent(id: string, params: {
  courseId: string;
  name?: string;
  systemPrompt?: string;
}): Promise<AgentDTO> {
  const res = await fetch(apiUrl(`/api/provenance/agents/${encodeURIComponent(id)}`), {
    ...fetchInit,
    method: "PATCH",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(params),
  });
  if (!res.ok) throw await apiError(res);
  const body = (await res.json()) as { agent: AgentDTO };
  return body.agent;
}

export async function deleteAgent(courseId: string, id: string): Promise<void> {
  const res = await fetch(
    apiUrl(
      `/api/provenance/agents/${encodeURIComponent(id)}?courseId=${encodeURIComponent(courseId)}`,
    ),
    { ...fetchInit, method: "DELETE" },
  );
  if (!res.ok) throw await apiError(res);
}

export async function listConversations(
  documentId: string,
  courseId: string,
  signal?: AbortSignal,
): Promise<ConversationDTO[]> {
  const res = await fetch(
    apiUrl(
      `/api/provenance/documents/${encodeURIComponent(documentId)}/conversations?courseId=${encodeURIComponent(courseId)}`,
    ),
    { ...fetchInit, signal },
  );
  if (!res.ok) throw await apiError(res);
  const body = (await res.json()) as { conversations: ConversationDTO[] };
  return body.conversations;
}

export async function createConversation(
  documentId: string,
  courseId: string,
  agentId: string,
): Promise<ConversationDTO> {
  const res = await fetch(
    apiUrl(`/api/provenance/documents/${encodeURIComponent(documentId)}/conversations`),
    {
      ...fetchInit,
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ courseId, agentId }),
    },
  );
  if (!res.ok) throw await apiError(res);
  const body = (await res.json()) as { conversation: ConversationDTO };
  return body.conversation;
}

export async function updateConversation(
  courseId: string,
  id: string,
  title: string,
): Promise<ConversationDTO> {
  const res = await fetch(
    apiUrl(`/api/provenance/conversations/${encodeURIComponent(id)}`),
    {
      ...fetchInit,
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ courseId, title }),
    },
  );
  if (!res.ok) throw await apiError(res);
  const body = (await res.json()) as { conversation: ConversationDTO };
  return body.conversation;
}

export async function deleteConversation(courseId: string, id: string): Promise<void> {
  const res = await fetch(
    apiUrl(
      `/api/provenance/conversations/${encodeURIComponent(id)}?courseId=${encodeURIComponent(courseId)}`,
    ),
    { ...fetchInit, method: "DELETE" },
  );
  if (!res.ok) throw await apiError(res);
}

export async function listMessages(
  conversationId: string,
  courseId: string,
  signal?: AbortSignal,
): Promise<MessageDTO[]> {
  const res = await fetch(
    apiUrl(
      `/api/provenance/conversations/${encodeURIComponent(conversationId)}/messages?courseId=${encodeURIComponent(courseId)}`,
    ),
    { ...fetchInit, signal },
  );
  if (!res.ok) throw await apiError(res);
  const body = (await res.json()) as { messages: MessageDTO[] };
  return body.messages;
}

export interface SendMessageCallbacks {
  onStarted?: (data: { conversationId: string; agent: string }) => void;
  onDelta: (text: string) => void;
  onDone?: (data: { assistantMessageId: string; title: string | null }) => void;
  onError?: (message: string) => void;
  /** Session lapsed (401). Send the user to sign in; don't render a message. */
  onAuthRequired?: () => void;
}

/**
 * Stream a chat turn. Returns an abort function the caller can invoke to
 * cut the connection (cleanly aborts the underlying fetch).
 *
 * Wire format: SSE with `event: <name>` + `data: <json>` frames matching
 * the existing /api/conversations/:id/messages stream so reuse is possible.
 *
 * `byoKey` (slice 5): when the student has supplied their own provider key
 * it rides along in the X-Provenance-LLM-Key header for this request only.
 * It lives in the browser (localStorage) and is never sent anywhere except
 * this proxied request; the worker uses it transiently and never stores it.
 */
export function streamChatTurn(
  conversationId: string,
  courseId: string,
  content: string,
  cb: SendMessageCallbacks,
  byoKey?: string | null,
): () => void {
  const ctrl = new AbortController();
  (async () => {
    try {
      const headers: Record<string, string> = { "content-type": "application/json" };
      if (byoKey) headers["x-provenance-llm-key"] = byoKey;
      const res = await fetch(
        apiUrl(`/api/provenance/conversations/${encodeURIComponent(conversationId)}/messages`),
        {
          ...fetchInit,
          method: "POST",
          headers,
          body: JSON.stringify({ courseId, content }),
          signal: ctrl.signal,
        },
      );
      if (!res.ok || !res.body) {
        // A 401 here means the session lapsed mid-conversation. Report it as
        // an auth failure so the caller can send the student to sign in
        // instead of printing "Unauthorized" into the transcript.
        if (res.status === 401) {
          cb.onAuthRequired?.();
          return;
        }
        cb.onError?.((await readError(res)).message);
        return;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        let sep: number;
        while ((sep = buf.indexOf("\n\n")) >= 0) {
          const frame = buf.slice(0, sep);
          buf = buf.slice(sep + 2);
          dispatch(frame, cb);
        }
      }
      if (buf.length > 0) dispatch(buf, cb);
    } catch (e) {
      // Abort path: AbortController.abort() rejects in-flight fetch and
      // reader.read() with either an AbortError DOMException OR a generic
      // DOMException whose message is "signal is aborted without reason"
      // (the latter when the abort had no explicit reason). Both are
      // benign — treat them as a clean cancel, not a stream failure.
      if (
        ctrl.signal.aborted ||
        (e as Error).name === "AbortError" ||
        /aborted/i.test((e as Error).message ?? "")
      ) {
        return;
      }
      cb.onError?.(e instanceof Error ? e.message : "stream failed");
    }
  })();
  return () => ctrl.abort();
}

function dispatch(frame: string, cb: SendMessageCallbacks) {
  let eventName = "message";
  let dataLine = "";
  for (const line of frame.split("\n")) {
    if (line.startsWith("event:")) eventName = line.slice(6).trim();
    else if (line.startsWith("data:")) dataLine += line.slice(5).trim();
  }
  if (!dataLine) return;
  let data: unknown;
  try { data = JSON.parse(dataLine); } catch { return; }
  switch (eventName) {
    case "started":
      cb.onStarted?.(data as { conversationId: string; agent: string });
      break;
    case "delta":
      cb.onDelta((data as { text: string }).text);
      break;
    case "done":
      cb.onDone?.(data as { assistantMessageId: string; title: string | null });
      break;
    case "error":
      cb.onError?.((data as { message: string }).message);
      break;
  }
}

// ─── Submissions (slice 6) ──────────────────────────────────────────────

export type RenderOrigin = "human" | "llm" | "pasted" | "edited";
export interface RenderRun { origin: RenderOrigin; length: number }
/** One clipboard import, with how much of it survives in the final text. */
export interface PasteRecordDTO {
  seq: number;
  at: number;
  sample: string;
  length: number;
  /** 0..1 — still present as literal text. */
  verbatim: number;
  /** 0..1 — survives reworded rather than literal. */
  nearMatch: number;
}

/** Descriptive facts about the event log. Never a verdict — see the README. */
export interface ProvenanceAuditDTO {
  sessions: number;
  spanMs: number;
  activeMs: number;
  longestGapMs: number;
  finalSessionShare: number;
  lengthDrift: number;
  fastBursts: number;
  orderingAnomalies: number;
  unverifiedMoves: number;
}

export interface ProvenanceRenderDTO {
  /** Render schema version. Absent on snapshots frozen before slice 8. */
  v?: number;
  text: string;
  runs: RenderRun[];
  pastes?: PasteRecordDTO[];
  audit?: ProvenanceAuditDTO;
}

export interface SubmissionSummary {
  token: string;
  createdAt: number;
  revokedAt: number | null;
  /** Whether the caller may revoke this link. Student-created links are
   *  permanent, so this is false for students (see the worker). */
  canRevoke: boolean;
}

/** Result of minting. `late` is the server's read of the checkpoint deadline at
 *  submit time — reported back so the student sees the same bare fact the
 *  instructor will, never as a refusal. */
export interface MintResult {
  token: string;
  createdAt: number;
  assignmentId: string | null;
  checkpointId: string | null;
  late: boolean;
}

/** Freeze a snapshot. `attach` is optional: omitted, the submission belongs to
 *  no assignment, which is how every submission worked before assignments and
 *  how it still works in a course that has none. */
export async function mintSubmission(
  documentId: string,
  courseId: string,
  attach?: { assignmentId: string; checkpointId: string },
): Promise<MintResult> {
  const res = await fetch(
    apiUrl(`/api/provenance/documents/${encodeURIComponent(documentId)}/submissions`),
    {
      ...fetchInit,
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ courseId, ...attach }),
    },
  );
  if (!res.ok) throw await apiError(res);
  return (await res.json()) as MintResult;
}

export async function listSubmissions(
  documentId: string,
  courseId: string,
  signal?: AbortSignal,
): Promise<SubmissionSummary[]> {
  const res = await fetch(
    apiUrl(
      `/api/provenance/documents/${encodeURIComponent(documentId)}/submissions?courseId=${encodeURIComponent(courseId)}`,
    ),
    { ...fetchInit, signal },
  );
  if (!res.ok) throw await apiError(res);
  const body = (await res.json()) as { submissions: SubmissionSummary[] };
  return body.submissions;
}

/** One submission checkpoint anywhere in the course, for the instructor list. */
export interface CourseSubmissionSummary {
  token: string;
  documentId: string;
  title: string;
  createdAt: number;
  revokedAt: number | null;
  studentUserId: string;
  studentEmail: string;
  studentName: string | null;
  /** Staff who have opened this snapshot, most recent first; empty = unread. */
  readBy: { by: string; at: number }[];
  origins: {
    total: number;
    human: number;
    llm: number;
    pasted: number;
    edited: number;
    /**
     * Number of clipboard imports. Shown on its own because students are asked
     * not to paste, so the count is a fact rather than an inference. Survival
     * percentages stay off this list — they need their source text beside them.
     */
    pasteCount: number;
  };
  /** Assignment context; all null when the submission is unattached. */
  assignmentId: string | null;
  assignmentTitle: string | null;
  checkpointId: string | null;
  checkpointName: string | null;
  /** `submittedAt > dueAt`, computed server-side on every read. */
  late: boolean;
}

/** Every submission checkpoint in the course, newest first. Instructor-only;
 *  the worker returns 403 for anyone else. */
export async function listCourseSubmissions(
  courseId: string,
  signal?: AbortSignal,
): Promise<CourseSubmissionSummary[]> {
  const res = await fetch(
    apiUrl(`/api/provenance/submissions?courseId=${encodeURIComponent(courseId)}`),
    { ...fetchInit, signal },
  );
  if (!res.ok) throw await apiError(res);
  const body = (await res.json()) as { submissions: CourseSubmissionSummary[] };
  return body.submissions;
}

export async function revokeSubmission(token: string): Promise<void> {
  const res = await fetch(
    apiUrl(`/api/provenance/submissions/${encodeURIComponent(token)}`),
    { ...fetchInit, method: "DELETE" },
  );
  if (!res.ok) throw await apiError(res);
}

// Share-token reads. Instructor-only despite the legacy "public" path segment —
// they send credentials and 401 when there's no session.
export interface PublicSubmissionDTO {
  title: string;
  createdAt: number;
  render: ProvenanceRenderDTO;
  courseId: string;
  /** Null for a snapshot that belongs to no assignment. */
  assignmentId: string | null;
  checkpointId: string | null;
  student: { userId: string; email: string; displayName: string | null };
}

/** Sentinel thrown by getPublicSubmission on a 401, so the viewer can tell
 *  "you need to sign in" apart from "this link isn't available to you". */
export const SUBMISSION_SIGN_IN_REQUIRED = "sign-in-required";

export async function getPublicSubmission(
  token: string,
  signal?: AbortSignal,
): Promise<PublicSubmissionDTO> {
  // Sends credentials: these endpoints are instructor-gated now, despite the
  // legacy "public" path segment. Without the session cookie even an instructor
  // reads as anonymous on the cross-origin deploy.
  const res = await fetch(
    apiUrl(`/api/provenance/public/submissions/${encodeURIComponent(token)}`),
    { ...fetchInit, signal },
  );
  // 401 is distinguishable so the viewer can prompt sign-in instead of showing a
  // dead "unavailable" card to an instructor who simply has no session yet.
  if (res.status === 401) throw new Error(SUBMISSION_SIGN_IN_REQUIRED);
  if (!res.ok) throw await apiError(res);
  return (await res.json()) as PublicSubmissionDTO;
}

export interface PublicConversationDTO {
  id: string;
  agentName: string;
  title: string | null;
  messages: { role: "user" | "assistant"; content: string }[];
}

export async function getPublicSubmissionConversations(
  token: string,
  signal?: AbortSignal,
): Promise<PublicConversationDTO[]> {
  const res = await fetch(
    apiUrl(`/api/provenance/public/submissions/${encodeURIComponent(token)}/conversations`),
    { ...fetchInit, signal },
  );
  if (!res.ok) throw await apiError(res);
  const body = (await res.json()) as { conversations: PublicConversationDTO[] };
  return body.conversations;
}

// ── Course settings (hide-marks toggle) ─────────────────────────────────

export interface ProvenanceSettings {
  hideProvenanceMarks: boolean;
  /** Whether the writing editor offers the LLM chat pane. Default on. */
  chatEnabled: boolean;
  /** Non-null = every student gets this one agent; null = students choose. */
  lockedAgentId: string | null;
}

/** Read the course's provenance settings. Any enrolled user. */
export async function getProvenanceSettings(
  courseId: string,
  signal?: AbortSignal,
): Promise<ProvenanceSettings> {
  const res = await fetch(
    apiUrl(`/api/provenance/settings?courseId=${encodeURIComponent(courseId)}`),
    { ...fetchInit, signal },
  );
  if (!res.ok) throw await apiError(res);
  return (await res.json()) as ProvenanceSettings;
}

/** Set the chat switch and/or the voice policy. Instructor only. Only the
 *  fields present are written, so the two controls can't clobber each other. */
export async function setProvenanceChatSettings(
  courseId: string,
  patch: { chatEnabled?: boolean; lockedAgentId?: string | null },
): Promise<{ chatEnabled: boolean; lockedAgentId: string | null }> {
  const res = await fetch(apiUrl(`/api/provenance/settings`), {
    ...fetchInit,
    method: "PATCH",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ courseId, ...patch }),
  });
  if (!res.ok) throw await apiError(res);
  return (await res.json()) as { chatEnabled: boolean; lockedAgentId: string | null };
}

/** Set the "hide marks from students" flag. Instructor only (403 otherwise). */
export async function setProvenanceHideMarks(
  courseId: string,
  hide: boolean,
): Promise<{ hideProvenanceMarks: boolean }> {
  const res = await fetch(apiUrl(`/api/provenance/settings`), {
    ...fetchInit,
    method: "PATCH",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ courseId, hideProvenanceMarks: hide }),
  });
  if (!res.ok) throw await apiError(res);
  return (await res.json()) as { hideProvenanceMarks: boolean };
}

// ── Assignments ─────────────────────────────────────────────────────────
//
// An assignment is one piece of writing with N checkpoints. "Draft due Monday,
// final due Wednesday" is one assignment with two checkpoints — the student
// keeps a single document across both.

export interface CheckpointDTO {
  id: string;
  name: string;
  /** Epoch ms, or null for no deadline — such a checkpoint is never late. */
  dueAt: number | null;
}

export interface AssignmentDTO {
  id: string;
  courseId: string;
  title: string;
  instructions: string;
  /** In the instructor's chosen order, which need not be date order. */
  checkpoints: CheckpointDTO[];
  archivedAt: number | null;
  createdAt: number;
  updatedAt: number;
  /** Whether students get the LLM chat beside this assignment's documents. */
  chatEnabled: boolean;
  /** Null = students choose their chat agent; else the one they all get. */
  lockedAgentId: string | null;
}

/** A checkpoint as the editor sends it. An existing checkpoint carries its
 *  `id` so the server updates it in place and the submissions already made to
 *  it stay attached; a new one has none. */
export interface CheckpointInput {
  id?: string;
  name: string;
  dueAt: number | null;
}

/** The course's assignments. Readable by students too — they need it to pick
 *  what they're submitting to. Archived ones are instructor-only. */
export async function listAssignments(
  courseId: string,
  opts?: { includeArchived?: boolean },
  signal?: AbortSignal,
): Promise<AssignmentDTO[]> {
  const qs = new URLSearchParams({ courseId });
  if (opts?.includeArchived) qs.set("includeArchived", "1");
  const res = await fetch(apiUrl(`/api/provenance/assignments?${qs}`), {
    ...fetchInit,
    signal,
  });
  if (!res.ok) throw await apiError(res);
  const body = (await res.json()) as { assignments: AssignmentDTO[] };
  return body.assignments;
}

/** "N of M submitted" figures for course staff. `byId` is keyed by
 *  checkpoint id (writing) or assignment id (code); absent = nobody yet. */
export interface SubmissionStats {
  /** Real students enrolled — the sample student is not counted. */
  students: number;
  /** `unread`: students whose latest submission no staff member has opened. */
  byId: Record<string, { submitted: number; onTime: number; unread: number }>;
}

/** The instructor's list: every assignment (drafts included) plus
 *  per-checkpoint submission counts. */
export async function listAssignmentsWithStats(
  courseId: string,
  signal?: AbortSignal,
): Promise<{ assignments: AssignmentDTO[]; stats: SubmissionStats }> {
  const qs = new URLSearchParams({ courseId, includeArchived: "1", stats: "1" });
  const res = await fetch(apiUrl(`/api/provenance/assignments?${qs}`), {
    ...fetchInit,
    signal,
  });
  if (!res.ok) throw await apiError(res);
  const body = (await res.json()) as { assignments: AssignmentDTO[]; stats?: SubmissionStats };
  return { assignments: body.assignments, stats: body.stats ?? { students: 0, byId: {} } };
}

export async function getAssignment(
  courseId: string,
  id: string,
  signal?: AbortSignal,
): Promise<AssignmentDTO> {
  const res = await fetch(
    apiUrl(
      `/api/provenance/assignments/${encodeURIComponent(id)}?courseId=${encodeURIComponent(courseId)}`,
    ),
    { ...fetchInit, signal },
  );
  if (!res.ok) throw await apiError(res);
  return (await res.json()) as AssignmentDTO;
}

/** Instructor only (403 otherwise). */
export async function createAssignment(params: {
  courseId: string;
  title: string;
  instructions: string;
  checkpoints: CheckpointInput[];
  chatEnabled?: boolean;
  lockedAgentId?: string | null;
  /** true = start as a draft students can't see; publish later. */
  archived?: boolean;
}): Promise<AssignmentDTO> {
  const res = await fetch(apiUrl(`/api/provenance/assignments`), {
    ...fetchInit,
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(params),
  });
  if (!res.ok) throw await apiError(res);
  return (await res.json()) as AssignmentDTO;
}

/** Instructor only. Supplying `checkpoints` makes the stored list match it,
 *  keyed by id; omitting it leaves the existing checkpoints alone. */
export async function updateAssignment(
  id: string,
  params: {
    courseId: string;
    title?: string;
    instructions?: string;
    archived?: boolean;
    checkpoints?: CheckpointInput[];
    chatEnabled?: boolean;
    lockedAgentId?: string | null;
  },
): Promise<AssignmentDTO> {
  const res = await fetch(apiUrl(`/api/provenance/assignments/${encodeURIComponent(id)}`), {
    ...fetchInit,
    method: "PATCH",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(params),
  });
  if (!res.ok) throw await apiError(res);
  return (await res.json()) as AssignmentDTO;
}

/** Instructor only. Submissions already attached survive and read as
 *  unattached — deleting an assignment never destroys a student's snapshot. */
export async function deleteAssignment(courseId: string, id: string): Promise<void> {
  const res = await fetch(
    apiUrl(
      `/api/provenance/assignments/${encodeURIComponent(id)}?courseId=${encodeURIComponent(courseId)}`,
    ),
    { ...fetchInit, method: "DELETE" },
  );
  if (!res.ok) throw await apiError(res);
}

/** One student's state at one checkpoint. `token` null = nothing submitted. */
export interface RosterCell {
  checkpointId: string;
  token: string | null;
  submittedAt: number | null;
  /** Submitted after the deadline. A bare fact, not a verdict. */
  late: boolean;
}

export interface RosterStudent {
  userId: string;
  email: string;
  displayName: string | null;
  /** One entry per checkpoint, in checkpoint order, submitted or not. */
  cells: RosterCell[];
}

export interface AssignmentRoster {
  assignment: AssignmentDTO;
  students: RosterStudent[];
}

/** Every enrolled student × every checkpoint — including students who have
 *  submitted nothing, which the course-wide submissions list cannot show.
 *  Instructor only. */
export async function getAssignmentRoster(
  courseId: string,
  id: string,
  signal?: AbortSignal,
): Promise<AssignmentRoster> {
  const res = await fetch(
    apiUrl(
      `/api/provenance/assignments/${encodeURIComponent(id)}/roster?courseId=${encodeURIComponent(courseId)}`,
    ),
    { ...fetchInit, signal },
  );
  if (!res.ok) throw await apiError(res);
  return (await res.json()) as AssignmentRoster;
}
